import { GuarantorCommunication, ProviderClaim, ProviderCollectionPayment, ProviderGuarantor } from '@enter/core';
import { mdiOpenInNew } from '@mdi/js';
import { PageLayout, PageLayoutRow, PageLayoutTitle } from 'components';
import _ from 'lodash';
import { inject } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useParams } from 'react-router-dom';
import { DialogContext, ErrorDialog } from 'services';
import { parseError } from 'services/dialog/util';
import { RootStore } from 'stores';
import { GuarantorService } from 'stores/guarantor.service';
import { Claims } from './claims';
import { Communications } from './communications';
import * as S from './details.styles';
import { Financial } from './financial';
import { Payments } from './payments';

// tslint:disable-next-line:no-empty-interface
interface Props {}

interface IInjectedProps {
    rootStore: RootStore;
}

export const GuarantorDetails = inject('rootStore')((props: Props) => {
    const {
        rootStore: { guarantorStore, claimStore },
    } = props as IInjectedProps;
    const { id } = useParams<{ id: string }>();
    const [guarantor, setGuarantor] = useState<ProviderGuarantor>();
    const [claims, setClaims] = useState<ProviderClaim[]>();
    const [payments, setPayments] = useState<ProviderCollectionPayment[]>();
    const [communications, setCommunications] = useState<GuarantorCommunication[]>();
    const [loadingPaymentPortal, setLoadingPaymentPortal] = useState<boolean>(false);
    const dialogContext = useContext(DialogContext);

    async function getAllGuarantorClaims(id: string) {
        const guarantorClaims = await GuarantorService.getAllGuarantorClaims(id);
        setClaims(guarantorClaims);

        return guarantorClaims;
    }

    useEffect(() => {
        (async () => {
            if (!id) {
                return;
            }

            Promise.all([
                getAllGuarantorClaims(id),
                GuarantorService.getAllGuarantorPayments(id).then(guarantorPayments => setPayments(guarantorPayments)),
                GuarantorService.getAllGuarantorCommunications(id).then(guarantorCommunications =>
                    setCommunications(guarantorCommunications),
                ),
            ]);

            const { item } = await guarantorStore.loadGuarantorDetail(id);
            setGuarantor(item);
        })();
    }, [id, guarantorStore]);

    const openPaymentPortal = async () => {
        setLoadingPaymentPortal(true);

        try {
            if (!id) {
                return;
            }

            const res = await GuarantorService.getCheckoutUrl(id);

            if (res?.url) {
                window.open(res.url, '_blank');
            }
        } catch (err) {
            const parsedError = await parseError(err);
            if (parsedError.payload.code === 'err.customer_portal_not_active') {
                const handleCloseDialog = () => {
                    dialogContext.hideDialog();
                };

                dialogContext.showDialog({
                    props: {
                        onClose: handleCloseDialog,
                        disableBackdropClick: true,
                        disableEscapeKeyDown: true,
                        renderTitle: 'The Guarantor Payment Portal is not available',
                    },
                    Component: (
                        <ErrorDialog
                            onClose={handleCloseDialog}
                            payload={{
                                message:
                                    'The Guarantor Payment Portal is not available for this person because they have not been placed into the patient collection flow yet.  If you feel this is in error, please reach out to support.',
                                hideTrace: true,
                            }}
                        />
                    ),
                });
            } else {
                dialogContext.showError(err);
            }
        }

        setLoadingPaymentPortal(false);
    };

    return (
        <PageLayout>
            <PageLayoutRow>
                {guarantor ? (
                    <S.Header>
                        <PageLayoutTitle>
                            {_.compact([guarantor.first, guarantor.middle, guarantor.last]).join(' ')}
                        </PageLayoutTitle>
                        <S.StyledButton
                            variant="primary"
                            size="medium"
                            disabled={loadingPaymentPortal}
                            onClick={openPaymentPortal}>
                            <S.StyledIcon path={mdiOpenInNew} size={0.75} />
                            <span>Guarantor Payment Portal</span>
                        </S.StyledButton>
                    </S.Header>
                ) : (
                    <ContentLoader speed={2} height={40} style={{ width: '100%' }} preserveAspectRatio="">
                        <rect x="0" y="0" rx="0" ry="0" width="100%" height="40" />
                    </ContentLoader>
                )}
            </PageLayoutRow>
            <PageLayoutRow>
                {claims ? (
                    <Financial claims={claims} />
                ) : (
                    <ContentLoader speed={2} height={100} style={{ width: '100%' }} preserveAspectRatio="">
                        <rect x="0" y="0" rx="0" ry="0" width="100%" height="100" />
                    </ContentLoader>
                )}
            </PageLayoutRow>
            <PageLayoutRow>
                <Communications communications={communications} />
            </PageLayoutRow>
            <PageLayoutRow>
                <Claims claims={claims} />
            </PageLayoutRow>
            <PageLayoutRow>
                <Payments payments={payments} />
            </PageLayoutRow>
        </PageLayout>
    );
});
